import React, { Fragment, ReactNode } from "react"

import useBreakpoint from "../../utils/hooks/use-breakpoint"
import { isString } from "../../utils/types"

type SpacingValue = number | string

type Props = {
  children: ReactNode
  separator?: ReactNode
  spacing?: SpacingValue | [SpacingValue, SpacingValue, SpacingValue]
  direction?: "column" | "row" | "column-reverse" | "row-reverse"
  className?: string
}

function Stack({
  children,
  separator,
  spacing = 0,
  direction = "column",
  className,
}: Props) {
  const { isMediumBreakpoint, isSmallBreakpoint } = useBreakpoint()
  const getSpacingValue = () => {
    if (!Array.isArray(spacing)) {
      return spacing
    }
    if (isMediumBreakpoint) {
      return spacing[1]
    }
    if (isSmallBreakpoint) {
      return spacing[2]
    }
    return spacing[0]
  }
  const spacingValue = getSpacingValue()
  const spacingString = isString(spacingValue)
    ? spacingValue
    : `${spacingValue}px`

  return (
    <div
      style={{ display: "flex", flexDirection: direction }}
      className={className}
    >
      {React.Children.toArray(children)
        .filter(child => !!child)
        .map((child, index) => {
          if (!React.isValidElement(child)) {
            throw new Error("Invalid element")
          }
          const sizedBox = !!spacing && (
            <div
              data-testid="SizedBox"
              style={{
                [direction?.includes("column") ? "height" : "width"]:
                  spacingString,
              }}
            />
          )
          return (
            <Fragment key={child.key}>
              {!!index && (
                <>
                  {!!separator && (
                    <>
                      {sizedBox}
                      {separator}
                    </>
                  )}
                  {sizedBox}
                </>
              )}
              {child}
            </Fragment>
          )
        })}
    </div>
  )
}

export default Stack
